import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { adminAxios, getOrderPin, setOrderPin, formatPence } from './ordering/orderingApi';
import usePageTitle from '@/hooks/usePageTitle';

const CHA_CHING = 'data:audio/wav;base64,UklGRmQCAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YUACAAB/f3+Af39/f4B/f3+Af3+Afn5/gIB+gH9/f4F+f4B/f4B/f3+AgX9/f4CAgIB/f4CAgH9+gH9/f4B/f3+Af3+AgIB+f4CAfn+Bf4B/f4B/f3+AgH9/f3+Af4B/f4CAf39/gIB/f4B/gH9/gIB/f39/gIB/gH+Af39/gIB/gH+AgH9/gIB/f3+AgIB/f4B/f3+AgIB/f39/gIB/f4B/f3+Af3+AgH9/f4CAgH9/f4CAgIB/f4CAgH9/gICAf3+AgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgH9/gICAf3+AgIB/f4CAgIB/f4CAgH9/gICAf39/gIB/f3+AgIB/f3+AgIB/f3+AgH9/f4CAfn9/gIB/f3+AgIB/f3+AgIB/f4CAgH9/gICAf3+AgIB/f4CAgH9/gIB/f3+Af39/gIB/f3+AgIB/f3+AgIB/f4CAgIB/f4CAgH9/gIB/f4CAgIB/f3+AgIB/f4CAgIB/f4CAgH9/gIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIA=';

export default function OrderDisplayPage() {
  usePageTitle('Order Display');
  const [authed, setAuthed] = useState(!!getOrderPin());
  const [pinInput, setPinInput] = useState('');
  const [orders, setOrders] = useState([]);
  const [now, setNow] = useState(Date.now());
  const lastIdsRef = useRef(new Set());
  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current = new Audio(CHA_CHING);
    audioRef.current.volume = 0.6;
  }, []);

  useEffect(() => {
    if (!authed) return;
    const fetch = async () => {
      try {
        const res = await adminAxios().get('/admin/order/board');
        const list = res.data.pending || [];
        const newIds = new Set(list.map((o) => o.id));
        // Detect new orders (present now but not previously)
        const isNew = [...newIds].some((id) => !lastIdsRef.current.has(id));
        if (isNew && lastIdsRef.current.size > 0) {
          try { audioRef.current?.play().catch(() => {}); } catch { /* noop */ }
        }
        lastIdsRef.current = newIds;
        setOrders(list);
      } catch (err) {
        if (err.response?.status === 401) {
          setOrderPin('');
          setAuthed(false);
          toast.error('Session expired');
        }
      }
    };
    fetch();
    const t = setInterval(fetch, 5000);
    const c = setInterval(() => setNow(Date.now()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [authed]);

  const handleComplete = async (id) => {
    try {
      await adminAxios().post('/admin/order/complete', { order_id: id });
      setOrders((prev) => prev.filter((o) => o.id !== id));
      toast.success('Marked complete');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!pinInput) return toast.error('Enter PIN');
    setOrderPin(pinInput);
    setAuthed(true);
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <form onSubmit={handleLogin} className="bg-white rounded-2xl p-8 shadow w-80 space-y-3">
          <h1 className="font-bold text-xl">Order Display</h1>
          <input
            type="password"
            value={pinInput}
            onChange={(e) => setPinInput(e.target.value)}
            placeholder="Admin PIN"
            className="w-full px-3 py-2 border rounded-lg"
            data-testid="display-pin-input"
          />
          <button className="w-full py-2 bg-black text-white rounded-lg font-semibold" data-testid="display-pin-submit">Enter</button>
        </form>
      </div>
    );
  }

  const ageStr = (created) => {
    if (!created) return '';
    const ms = now - new Date(created).getTime();
    if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
    if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
    return `${Math.floor(ms / 3600000)}h ago`;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white p-6">
      <header className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Making Now ({orders.length})</h1>
        <button onClick={() => { setOrderPin(''); setAuthed(false); }} className="text-xs underline opacity-70">Logout</button>
      </header>

      {orders.length === 0 ? (
        <p className="text-center opacity-60 py-20">No orders pending.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {orders.map((o) => (
            <div key={o.id} className="bg-gray-800 rounded-2xl p-4 flex flex-col" data-testid={`display-order-${o.id}`}>
              <div className="flex items-center justify-between">
                <span className="text-5xl font-black text-amber-400">{o.seat_number}</span>
                <span className="text-xs opacity-70">{ageStr(o.paid_at || o.created_at)}</span>
              </div>
              <div className="mt-2 text-lg font-semibold">{o.first_name} {o.last_name}</div>
              <ul className="mt-2 text-sm space-y-1 flex-1">
                {(o.items || []).map((it, i) => (
                  <li key={i}>
                    <div>{it.quantity}× {it.product_name}</div>
                    {it.choices?.length > 0 && (
                      <div className="text-xs opacity-70 ml-3">
                        {it.choices.map((c) => `${c.option_group_name}: ${c.choice_name}`).join(' • ')}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              <div className="flex items-center justify-between mt-3">
                <span className="text-sm opacity-70">{formatPence(o.total_pence)}</span>
                <button
                  onClick={() => handleComplete(o.id)}
                  className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-semibold"
                  data-testid={`display-complete-${o.id}`}
                >
                  ✓ Complete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
